import { BottomSheetModalProvider } from '@gorhom/bottom-sheet'
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query'
import { useGlobalSearchParams } from 'expo-router'
import { StrictMode, useEffect, useMemo, Fragment, type ReactNode } from 'react'
import { IntlProvider } from 'react-intl'
import { Platform } from 'react-native'
import { GestureHandlerRootView } from 'react-native-gesture-handler'
import { configureReanimatedLogger, ReanimatedLogLevel } from 'react-native-reanimated'

import { env } from '@/src/config/env'
import { SentryNavTracing, TouchEventBoundary, wrap } from '@/src/config/sentry/SentryNavTracing'
import { Stack } from '@/src/elements/navigation/Stack'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { authTokenStorage } from '@/src/features/auth/utils/authTokenStorage'
import { getIsLoggedIn } from '@/src/features/auth/utils/getIsLoggedIn'
import { loadToken } from '@/src/features/auth/utils/loadToken'
import { PublicationShareModalHost } from '@/src/features/publication/ui/components/PublicationShareModal/PublicationShareModalHost'
import { useIsRosarioUserQuery } from '@/src/features/rosario/services/useIsRosarioUserQuery'
import { BottomProvider } from '@/src/general/components/BottomContext'
import { PrayerPromptManager } from '@/src/general/components/PrayerPromptManager'
import { ToastProvider } from '@/src/general/components/ToastContext'
import { UserActionNetworkErrorToasts } from '@/src/general/components/UserActionNetworkErrorToasts'
import { UserProfileModalProvider } from '@/src/general/components/UserProfileModalContext/UserProfileModalContext'
import { BannersProvider } from '@/src/general/context/BannersProvider'
import { usePageType } from '@/src/general/hooks/usePageType'
import { setupDayjs } from '@/src/general/primitives/dates/dayjs'
import { ServiceWorker } from '@/src/general/service-worker/ServiceWorker'
import { fetcher } from '@/src/general/utilities/fetcher'
import { setAxiosInstanceLocale } from '@/src/general/utilities/fetcher/axiosInstance'
import { queryClient as appQueryClient } from '@/src/general/utilities/query'
import { Cookie } from '@/src/general/utils/cookies/Cookie'
import { filterSearchParams } from '@/src/general/utils/filterSearchParams'
import { GTM } from '@/src/general/utils/gtm'
import { LOGGED } from '@/src/general/utils/gtm/constants/Logged.enum'
import { getDisplayMode } from '@/src/general/utils/gtm/getDisplayMode'
import { isIOSWebview } from '@/src/general/utils/isIOSWebview'
import { RoutesProvider } from '@/src/general/utils/routes/RoutesProvider'
import { getMessages } from '@/src/lang/getMessages'
import { loadPolyfills } from '@/src/lang/Polyfills'
import { useRootLocale } from '@/src/lang/useRootLocale'

import '@/src/config/nativewind/cssInterop'

import { MailDeliveryIssueToast } from './MailDeliveryIssueToast'
import { PAGE_BACKGROUND_THEME_COLOR, PageTypeBackground } from './PageTypeBackground'
import { PlatformWrapper } from './PlatformWrapper'
import { ThemeProvider } from './ThemeProvider'
import { usePageViewAnalyticsTracking } from './usePageViewAnalyticsTracking'
import { usePatchMeAppType } from './usePatchMeAppType'
import { useSaveSponsorInfos } from './useSaveSponsorInfos'

import { defaultLocale } from '@/translations/translations.constants'

function loadGlobalCSS() {
  try {
    require('@/src/styles/global.css')
  } catch (e) {
    console.error(e)
  }
}

loadGlobalCSS()

loadPolyfills()

// Relax Reanimated's strict logger: NativeWind's CssInterop reads shared values during
// render, which trips the strict-mode "Reading from `value` during render" warning.
configureReanimatedLogger({ level: ReanimatedLogLevel.warn, strict: false })

// StrictMode Cause error on native : findHostInstance_DEPRECATED is deprectead
// see : https://github.com/software-mansion/react-native-reanimated/issues/6997
const StrictModeWebOnly = Platform.OS === 'web' ? StrictMode : Fragment

// Call outside react to be available for the app on first render
GTM.init({
  debug: __DEV__,
  code: env.EXPO_PUBLIC_CONF_KEY_GTM || '',
})

const SubProviders = (): ReactNode => {
  const queryClient = useQueryClient()
  const searchParams = useGlobalSearchParams()
  const { uik, utm_source: utmSource } = filterSearchParams(searchParams)
  const me = useMeQuery()
  useIsRosarioUserQuery()

  const locale = useRootLocale(me, utmSource)

  setAxiosInstanceLocale(locale)

  useSaveSponsorInfos(uik)

  usePatchMeAppType()

  usePageViewAnalyticsTracking(searchParams)

  useEffect(() => {
    const onTokenValidChange = (value: boolean) => {
      if (!value) {
        queryClient.invalidateQueries({ queryKey: ['me'] })
        authTokenStorage.removeToken()
        fetcher.removeXAuthToken()
        Cookie.clearAll()
      }
    }

    const unsubscribe = fetcher.isValidToken.addEventListener(onTokenValidChange)

    return unsubscribe
  }, [queryClient])

  useEffect(() => {
    const gtmData = JSON.parse(Cookie.get('gtmData') || 'null')

    GTM.sendEvent({
      displayMode: getDisplayMode(),
      lang: locale,
      isOnline: 1, // TODO: implement offline mode later on
      isLoggedIn: getIsLoggedIn() ? LOGGED.IN : LOGGED.OUT,
      ...(typeof gtmData === 'object' && gtmData),
    })

    if (gtmData) {
      Cookie.delete('gtmData', 'session')
    }
  }, [locale, me.data?.id])

  useEffect(() => setupDayjs(locale), [locale])

  const intlProviderValue = useMemo(() => ({ locale, defaultLocale, messages: getMessages(locale) }), [locale])
  const { background } = usePageType()

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <ThemeProvider colors={{ background: PAGE_BACKGROUND_THEME_COLOR[background] }}>
        {!isIOSWebview() && <ServiceWorker />}

        <IntlProvider {...intlProviderValue}>
          {/* Wait for user lang to be defined before rendering routes*/}
          <RoutesProvider lang={locale}>
            <BottomSheetModalProvider>
              <ToastProvider>
                <MailDeliveryIssueToast />
                <PageTypeBackground background={background} />
                <BannersProvider searchParams={searchParams}>
                  <UserProfileModalProvider searchParams={searchParams}>
                    <PrayerPromptManager />
                    <PublicationShareModalHost />
                    <UserActionNetworkErrorToasts />
                    <PlatformWrapper>
                      <Stack screenOptions={{ headerShown: false, freezeOnBlur: true }} />
                    </PlatformWrapper>
                  </UserProfileModalProvider>
                </BannersProvider>
              </ToastProvider>
            </BottomSheetModalProvider>
          </RoutesProvider>
        </IntlProvider>
      </ThemeProvider>
    </GestureHandlerRootView>
  )
}

loadToken()

const RootLayout = () => (
  <StrictModeWebOnly>
    <TouchEventBoundary>
      <BottomProvider>
        <QueryClientProvider client={appQueryClient}>
          <SentryNavTracing />
          <SubProviders />
        </QueryClientProvider>
      </BottomProvider>
    </TouchEventBoundary>
  </StrictModeWebOnly>
)

export const AppLayout = wrap(RootLayout)
